/**
 * Requirements summary:
 * Server-side timezone helpers for deriving a YYYY-MM-DD string in a gym's IANA
 * timezone — either "today" (getTodayIsoDateInTimezone) or the calendar day of
 * a specific instant (getIsoDateInTimezone, e.g. a UTC-stored events.start_time).
 * Vercel/Node servers run in UTC, so `new Date(...).toISOString().slice(0, 10)`
 * silently rolls forward to the next UTC day for evening times west of UTC.
 * Routes that drive gym-facing calendar dates (private lessons, standing series
 * start dates, team-practice closing-day matching, etc.) must convert to the
 * org's local calendar first.
 *
 * Kept dependency-free (only `Intl.DateTimeFormat`) so it is safe to import
 * from API routes, services, and tests without dragging in supabase/client.
 *
 * For the supabase lookup of an organization's timezone, prefer the existing
 * `getOrgTimezone` helper in `src/utils/cron/invoiceCronHelpers.ts` — it is
 * already lean (one `gymlocations` row) and uses the same default fallback.
 */

/**
 * Default IANA timezone when an organization has no gymlocations row, or no
 * timezone is configured on its primary location. Matches the cron helpers
 * default (`getOrgTimezone` in invoiceCronHelpers.ts) so the staff-direct
 * private lesson path agrees with the rest of the codebase.
 *
 * Eastern Time covers the largest share of CheerVue gyms today; gyms outside
 * ET should configure their primary location's timezone to avoid this fallback.
 */
export const DEFAULT_SERVER_TIMEZONE = 'America/New_York';

/**
 * Return today's date as a YYYY-MM-DD string in the given IANA timezone.
 *
 * Uses Intl.DateTimeFormat with the `en-CA` locale because that locale formats
 * dates as `YYYY-MM-DD` — the exact shape downstream date math expects.
 *
 * Falls back to UTC slicing only if the supplied timezone is invalid (Node
 * throws a RangeError on unknown timezones). Callers should pass a timezone
 * sourced from `getOrgTimezone` to keep that fallback inert.
 */
export function getTodayIsoDateInTimezone(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/**
 * Return the YYYY-MM-DD date of a specific instant as observed in the given
 * IANA timezone.
 *
 * Same `en-CA` + Intl.DateTimeFormat approach as getTodayIsoDateInTimezone,
 * but for an arbitrary instant rather than "now". Used wherever a UTC-stored
 * timestamptz (e.g. events.start_time) must be reduced to the local calendar
 * day: a 6:30pm America/New_York practice is stored as the next UTC day, so a
 * naive ISO slice would land it on the wrong calendar date.
 *
 * Falls back to UTC slicing only if the supplied timezone is invalid (Node
 * throws a RangeError on unknown timezones).
 */
export function getIsoDateInTimezone(
  instant: string | number | Date,
  timezone: string,
): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(new Date(instant));
  } catch {
    // The catch fallback must itself be safe: `new Date(instant).toISOString()`
    // throws a RangeError on a null / unparseable instant, which would escape
    // this catch and propagate. Guard the invalid-instant case and return ''
    // so callers can skip the row instead of crashing.
    const d = new Date(instant);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }
}

/** Gym-local minutes-since-midnight (server-resolved; the client never does tz math). */
export function gymLocalMinutesNow(timezone: string, now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now);
  const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
  const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hh * 60 + mm;
}

/**
 * Has a wall-clock start (`date` YYYY-MM-DD plus `startMinutes` after local
 * midnight) already been reached in `timezone` at `now`? A start at exactly
 * `now` counts as reached. Private-lesson slot lists and the standing-lesson
 * check pass the lesson LOCATION's zone here, never the server's clock, which
 * is UTC on Vercel (#6216). Pass a validated zone: Intl throws on a bad one.
 */
export function isLocalStartInPast(
  date: string,
  startMinutes: number,
  timezone: string,
  now: Date = new Date(),
): boolean {
  const today = getIsoDateInTimezone(now, timezone);
  if (date !== today) return date < today;
  return startMinutes <= gymLocalMinutesNow(timezone, now);
}

/**
 * UTC offset (in ms) that `timezone` observes at the given UTC instant.
 * Positive east of UTC, negative west (America/New_York → −5h EST / −4h EDT).
 * `atUtcMs` should be second-aligned — Intl parts carry no milliseconds.
 */
function tzOffsetMs(atUtcMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // 'h23' (not hour12: false) — some ICU versions render midnight as "24"
    // under hour12: false, which would corrupt Date.UTC below.
    hourCycle: 'h23',
  }).formatToParts(new Date(atUtcMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - atUtcMs;
}

/**
 * Resolve the UTC instant of a local wall-clock time in `timezone`.
 * Two-pass offset fix converges for DST transitions: a wall time that exists
 * uniquely maps to its exact instant; a repeated wall time (fall-back hour)
 * resolves deterministically to one of its two instants; a skipped wall time
 * (spring-forward gap) resolves to the equivalent shifted instant. Day
 * boundaries (midnight / 23:59:59.999) are unaffected by US DST transitions,
 * which occur at 2 a.m. local.
 */
function localWallTimeToUtc(
  yyyymmdd: string,
  hour: number,
  minute: number,
  second: number,
  ms: number,
  timezone: string,
): Date {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const wallAsUtcMs = Date.UTC(y, m - 1, d, hour, minute, second);
  try {
    const offset1 = tzOffsetMs(wallAsUtcMs, timezone);
    let utcMs = wallAsUtcMs - offset1;
    const offset2 = tzOffsetMs(utcMs, timezone);
    if (offset2 !== offset1) utcMs = wallAsUtcMs - offset2;
    return new Date(utcMs + ms);
  } catch {
    // Invalid timezone → interpret the wall time as UTC (the pre-#1933
    // behavior), mirroring the fallbacks in the helpers above.
    return new Date(wallAsUtcMs + ms);
  }
}

/**
 * Noon on a gym-local calendar day, as a UTC ISO instant. Used to store a
 * date-only fact (the date written on a paper waiver) in a timestamp column so
 * every screen, in any zone a few hours either side, shows the same day.
 */
export function localNoonToUtcIso(date: string, timezone: string): string {
  return localWallTimeToUtc(date, 12, 0, 0, 0, timezone).toISOString();
}

/**
 * Convert a date-only report range (YYYY-MM-DD start/end, inclusive) into the
 * pair of UTC instants that bound the GYM-LOCAL days (#1933):
 *
 *   startInstant = local midnight (00:00:00.000) of `startDate` in `timezone`
 *   endInstant   = local end-of-day (23:59:59.999) of `endDate` in `timezone`
 *
 * Both are returned as ISO strings suitable for PostgREST gte/lte filters on
 * timestamptz columns. For a July 1–31 Eastern range this yields
 * 2026-07-01T04:00:00.000Z → 2026-08-01T03:59:59.999Z, so a payment at
 * 2026-07-01T01:00:00Z (June 30, 9 p.m. Eastern) correctly falls OUTSIDE the
 * window. For timezone 'UTC' this reduces exactly to the previous behavior
 * (`<start>T00:00:00.000Z` / `<end>T23:59:59.999Z`). DST is handled per
 * boundary: each endpoint uses the offset in force on THAT local day, so a
 * March range in America/New_York starts at UTC−5 (EST) and ends at UTC−4
 * (EDT). An invalid timezone falls back to interpreting both bounds as UTC.
 */
export function localDateRangeToUtcInstants(
  startDate: string,
  endDate: string,
  timezone: string,
): { startInstant: string; endInstant: string } {
  return {
    startInstant: localWallTimeToUtc(startDate, 0, 0, 0, 0, timezone).toISOString(),
    endInstant: localWallTimeToUtc(endDate, 23, 59, 59, 999, timezone).toISOString(),
  };
}
